import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, apiFormData } from '@/lib/api';
import { categoryDetailHref, categoryFormBackTo } from '@/lib/category-navigation';
import { categorySuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface CategoryForm {
  name: string;
  slug: string;
  description?: string;
  parentId?: string;
  status?: string;
}

interface CategoryRecord extends CategoryForm {
  id: string;
  name: string;
  imageUrl?: string | null;
}

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

function flattenForSelect(nodes: CategoryNode[], depth = 0, excludeId?: string): { value: string; label: string }[] {
  return nodes.flatMap((node) => {
    if (node.id === excludeId) return node.children ? flattenForSelect(node.children, depth + 1, excludeId) : [];
    return [
      { value: node.id, label: `${'— '.repeat(depth)}${node.name}` },
      ...(node.children ? flattenForSelect(node.children, depth + 1, excludeId) : []),
    ];
  });
}

function buildCategoryFormData(values: CategoryForm, imageFile: File | null, removeImage: boolean) {
  const formData = new FormData();
  formData.append('name', values.name);
  if (values.slug.trim()) formData.append('slug', values.slug.trim());
  if (values.description?.trim()) formData.append('description', values.description.trim());
  if (values.status) formData.append('status', values.status);
  if (values.parentId) formData.append('parentId', values.parentId);
  if (imageFile) formData.append('image', imageFile);
  if (removeImage) formData.append('remove_image', '1');
  return formData;
}

export function CategoryFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const isEdit = Boolean(id);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [fileLabel, setFileLabel] = useState('Haga clic para subir o arrastre y suelte');
  const [removeImage, setRemoveImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: tree } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryNode[]>('/categories'),
  });

  const { data: existing, isLoading } = useQuery({
    queryKey: ['categories', id],
    queryFn: () => api<CategoryRecord>(`/categories/${id}`),
    enabled: isEdit,
  });

  const { register, handleSubmit, watch } = useForm<CategoryForm>({
    values: existing ?? {
      name: '',
      slug: '',
      description: '',
      parentId: params.get('parent_id') ?? '',
      status: 'active',
    },
  });

  const nameValue = watch('name');

  const mutation = useMutation({
    mutationFn: (values: CategoryForm) => {
      const formData = buildCategoryFormData(values, imageFile, removeImage);
      return isEdit
        ? apiFormData<CategoryRecord>(`/categories/${id}`, formData, { method: 'PATCH' })
        : apiFormData<CategoryRecord>('/categories', formData, { method: 'POST' });
    },
    onSuccess: (created) => {
      toast(categorySuccessToast(isEdit ? 'edit' : 'create'));
      void queryClient.invalidateQueries({ queryKey: ['categories'] });
      if (isEdit && id) {
        void queryClient.invalidateQueries({ queryKey: ['categories', id] });
        navigate(categoryFormBackTo({ editedCategoryId: id }));
        return;
      }
      if (created?.id) {
        navigate(categoryFormBackTo({ editedCategoryId: created.id }));
        return;
      }
      navigate(categoryFormBackTo({ parentId: params.get('parent_id') }));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, isEdit ? 'No se pudo guardar la categoría' : 'No se pudo crear la categoría'));
    },
  });

  const parentOptions = [{ value: '', label: 'Sin categoría padre' }, ...flattenForSelect(tree ?? [], 0, id)];
  const backTo = categoryFormBackTo({
    editedCategoryId: isEdit ? id : null,
    parentId: params.get('parent_id'),
  });
  const currentImageUrl = !removeImage ? (previewUrl ?? existing?.imageUrl ?? null) : null;

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setImageFile(file);
    setRemoveImage(false);
    setFileLabel(file ? file.name : 'Haga clic para subir o arrastre y suelte');
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  }

  return (
    <div className="lms-category-form">
      <div className="lms-category-form__shell">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <HubBackLink to={backTo} label="Volver a categorías" />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
                {isEdit ? 'Editar categoría' : 'Nueva categoría'}
              </h1>
              <p className="mt-1 text-dash-on-surface-variant">
                {isEdit
                  ? `${existing?.name ?? ''} — actualice los datos de la categoría`
                  : 'Crear una categoría en el catálogo académico'}
              </p>
            </div>
          </div>
          {isEdit && (
            <Link
              to={id ? categoryDetailHref(id) : '/categorias'}
              className="lms-category-form__btn lms-category-form__btn--cancel sm:ml-4 sm:shrink-0"
            >
              <span className="material-symbols-outlined text-lg" aria-hidden="true">
                visibility
              </span>
              Ver detalle
            </Link>
          )}
        </div>

        {isEdit && isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="lms-category-form__card">
            <div className="lms-category-form__fields">
              <div className="lms-category-form__grid">
                <div className="lms-category-form__field lms-category-form__field--full">
                  <label className="lms-category-form__label" htmlFor="category-name">
                    Nombre <span className="text-[#ba1a1a]">*</span>
                  </label>
                  <input
                    id="category-name"
                    type="text"
                    required
                    placeholder="Ej. Ciencias Aplicadas"
                    className="lms-category-form__input"
                    {...register('name', { required: true })}
                  />
                </div>

                <div className="lms-category-form__field lms-category-form__field--full">
                  <label className="lms-category-form__label" htmlFor="category-slug">
                    Slug
                  </label>
                  <input
                    id="category-slug"
                    type="text"
                    placeholder={
                      nameValue
                        ? nameValue
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .trim()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/[\s_-]+/g, '-')
                            .replace(/^-+|-+$/g, '') || 'ej-ciencias-aplicadas'
                        : 'ej-ciencias-aplicadas'
                    }
                    className="lms-category-form__input"
                    {...register('slug')}
                  />
                  <p className="lms-category-form__hint">Se genera automáticamente si se deja vacío</p>
                </div>

                <div className="lms-category-form__field lms-category-form__field--full">
                  <label className="lms-category-form__label" htmlFor="category-description">
                    Descripción
                  </label>
                  <textarea
                    id="category-description"
                    rows={4}
                    placeholder="Breve descripción del propósito de esta categoría..."
                    className="lms-category-form__input lms-category-form__textarea"
                    {...register('description')}
                  />
                </div>

                <div className="lms-category-form__field">
                  <label className="lms-category-form__label" htmlFor="category-status">
                    Estado <span className="text-[#ba1a1a]">*</span>
                  </label>
                  <select
                    id="category-status"
                    required
                    className="lms-category-form__input lms-category-form__select"
                    {...register('status', { required: true })}
                  >
                    <option value="active">Activa</option>
                    <option value="inactive">Inactiva</option>
                  </select>
                </div>

                <div className="lms-category-form__field">
                  <label className="lms-category-form__label" htmlFor="category-parent">
                    Categoría padre <span className="lms-category-form__label-muted">(Opcional)</span>
                  </label>
                  <select
                    id="category-parent"
                    className="lms-category-form__input lms-category-form__select"
                    {...register('parentId')}
                  >
                    {parentOptions.map((opt) => (
                      <option key={opt.value || 'root'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lms-category-form__field lms-category-form__field--full">
                  <span className="lms-category-form__label">
                    Imagen de categoría <span className="lms-category-form__label-muted">(Opcional)</span>
                  </span>

                  {currentImageUrl && (
                    <div className="mb-4 flex flex-wrap items-center gap-4">
                      <AuthenticatedImage
                        src={currentImageUrl}
                        alt={existing?.name ?? 'Vista previa'}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                      {isEdit && existing?.imageUrl && !imageFile && (
                        <label className="inline-flex items-center gap-2 text-sm text-[#454651]">
                          <input
                            type="checkbox"
                            checked={removeImage}
                            onChange={(event) => {
                              setRemoveImage(event.target.checked);
                              if (event.target.checked) {
                                setPreviewUrl(null);
                              }
                            }}
                            className="rounded border-[#c6c5d3]"
                          />
                          Eliminar imagen actual
                        </label>
                      )}
                    </div>
                  )}

                  <label className="lms-category-form__dropzone" htmlFor="category-image">
                    <span className="material-symbols-outlined mb-3 text-4xl text-[#454651]" aria-hidden="true">
                      cloud_upload
                    </span>
                    <p className="mb-2 text-sm font-medium text-[#1a1c1e]">{fileLabel}</p>
                    <p className="text-xs font-semibold text-[#454651]">SVG, PNG, JPG (MÁX. 800×400px)</p>
                    <input
                      id="category-image"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="lms-category-form__actions">
              <Link to={backTo} className="lms-category-form__btn lms-category-form__btn--cancel">
                Cancelar
              </Link>
              <button type="submit" className="lms-category-form__btn lms-category-form__btn--submit" disabled={mutation.isPending}>
                {isEdit ? 'Guardar cambios' : 'Crear categoría'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
